'use client';

import {
  AppearanceType,
  ButtonTextLink,
  HeadingAndCta,
} from '@fdc/__generated__/graphql';
import { useAppRouter } from '@fdc/hooks/useAppRouter';
import {
  convertToCoreButtonProps,
  headingToFeatureTitleProps,
  markdownToFeatureMarkdownRendererProps,
} from '@fdc/utils/content-type-mapper-utils';
import FeatureIntroText from '@woolworthslimited/core-components-react/intro-text/intro-text';
import { FeatureIntroTextProps } from '@woolworthslimited/core-components-react/intro-text/intro-text.types';
import { useWindowDimension } from '@woolworthslimited/core-components-react/shared/hooks/useWindowDimension';

import {
  isMediumDeviceOrGreater,
  isSmallDeviceOrGreater,
} from '@contentservice/shared/services/breakpoint/breakpoint.service';

type HeadingCtaProps = HeadingAndCta & {
  align?: FeatureIntroTextProps['align'];
  onClick?: React.MouseEventHandler<HTMLElement>;
};

export const HeadingCta: React.FC<HeadingCtaProps> = ({
  heading,
  headingSize,
  bodyText,
  cta,
  align,
  onClick,
}) => {
  const router = useAppRouter();
  const [width] = useWindowDimension(false);
  if (!width) {
    return null;
  }

  const isTabletOrLarger = isSmallDeviceOrGreater(width);
  const isDesktop = isMediumDeviceOrGreater(width);
  const size = headingSize === 'H1' ? 'large' : 'medium';
  const buttonVariant = cta?.variant || 'Primary';

  const buttonInput: ButtonTextLink | null = cta
    ? {
        label: cta.label ?? '',
        link: cta.link ?? '',
        ariaLabel: cta.ariaLabel ?? '',
        target: cta.target,
        rel: cta.rel,
        style: buttonVariant,
        appearance: AppearanceType.Button,
        variant: buttonVariant,
      }
    : null;

  const coreButtonProps = buttonInput
    ? convertToCoreButtonProps(buttonInput)
    : null;

  const getAlign = (): FeatureIntroTextProps['align'] => {
    if (align) {
      return align;
    }
    if (!isDesktop) {
      return 'center';
    }
    // space-between puts heading and body on one row; only use it when a CTA
    // should sit opposite the heading
    if (cta) {
      return 'space-between';
    }
    return 'left';
  };

  const introTextProps: FeatureIntroTextProps = {
    size,
    heading: headingToFeatureTitleProps(heading, headingSize, size),
    body: bodyText
      ? markdownToFeatureMarkdownRendererProps(bodyText)
      : undefined,
    // Only add CTA if we successfully mapped the button props
    cta: coreButtonProps
      ? {
          ...coreButtonProps,
          isFullWidth: !isTabletOrLarger,
          clickHandler: (e) => {
            e.preventDefault();
            cta.target === '_blank'
              ? window.open(cta.link, '_blank', 'noopener,noreferrer')
              : router.push(cta.link);
            onClick?.(e);
          },
        }
      : undefined,
    align: getAlign(),
  };

  return <FeatureIntroText {...introTextProps} />;
};

export default HeadingCta;
